import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  skip,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ORBI_HOW_IT_WORKS_MESSAGE_ID } from '@patient-ui/shared/constants';
import { OrbiFeedbackType } from '@patient-ui/shared/enums';
import {
  orbiMessageLocksChat,
  parseOrbiAgentResponse,
  recordOrbiFeedback,
  writeOrbiSessionLock,
} from '@patient-ui/shared/utils';

import { OrbiError } from './orbi-error';
import { OrbiActions } from './orbi.actions';
import {
  selectOrbiLastRequestContext,
  selectOrbiLastUserMessage,
  selectOrbiSessionId,
} from './orbi.selectors';
import { OrbiService } from './orbi.service';
import { selectOrbiAgentAvailable } from '../orbi-feature/orbi-feature.selectors';
import { selectPrimaryAccountHolder } from '../patient/patient.selectors';

/**
 * Orbi chat effects (PCSA-13164) — the send/receive transport. Mirrors the
 * `results` effect conventions: flattening operator, service call, `map` to a
 * success action, `catchError -> of(failure)` so the stream never dies.
 *
 * `sessionId` comes from state (never empty). `requestContext` is supplied by
 * the caller in the action (mirrors mobile's `addMessage(text, {requestContext})`,
 * where the shell reads the ambient screen context) — the chat UI derives it
 * with the pure `matchOrbiRequestContext` helper, so this layer owns no router
 * knowledge.
 */
@Injectable()
export class OrbiEffects {
  constructor(
    private actions$: Actions,
    private orbiService: OrbiService,
    private store: Store
  ) {}

  /**
   * Emits whenever the ACTIVE conversation changes — the user started a new one,
   * resumed one from history, or logged out.
   *
   * Derived from the sessionId itself rather than from the three actions that
   * change it, so a fourth way of changing conversation cannot slip past it (the
   * id IS the conversation, by definition). `skip(1)` drops the current value
   * every store selector replays on subscribe, so only a real change counts.
   */
  private readonly conversationChanged$ = this.store
    .select(selectOrbiSessionId)
    .pipe(distinctUntilChanged(), skip(1));

  /**
   * `concatMap`, not `exhaustMap` (PCSA-13312, 08/14 Vibe: "Ensure the user can
   * type and send a second message while the agent is still generating the
   * first, with responses queued into their own bubbles").
   *
   * `exhaustMap` DROPPED a send that arrived while one was in flight — the
   * message was appended to the thread by the reducer and then silently never
   * asked, which is why the input had to be disabled during loading in the first
   * place. `concatMap` QUEUES instead: each message is still sent one at a time,
   * in the order the user sent them, and each answer arrives in its own turn.
   *
   * Not `mergeMap`: the agent's session is a conversation, so two questions
   * running concurrently could return answers out of order and each would be
   * answered without the other's turn in the session's context.
   *
   * `withLatestFrom` reads the sessionId when the ACTION fires rather than when
   * the request starts, so a queued message is tagged with the conversation the
   * user actually sent it from — see `sendMessageSuccess`.
   *
   * A send the user ABANDONS by leaving that conversation mid-flight is dropped
   * here rather than merely ignored downstream. The reducer already guards all
   * three outcomes on the sessionId, so a late answer could never land on the
   * wrong thread — but without these two checks the request itself still ran, and
   * because `concatMap` is serial, the new conversation's first question sat in
   * the queue behind an answer that was going to be thrown away. Both ends are
   * covered:
   *   - QUEUED but not yet started: the active conversation is re-read when this
   *     send reaches the front of the queue and the send is skipped if it no
   *     longer belongs to it (a definitive prevention — nothing is sent).
   *   - Already IN FLIGHT: `takeUntil` completes the inner observable, which
   *     unsubscribes the HttpClient request and frees the queue. Best-effort as
   *     to the gateway — the POST is already out, so the agent may still process
   *     it — but the client stops waiting on it either way.
   * Dropping the outcome is safe for the thread: both `startNewConversation` and
   * `resumeConversation` replace `messages` wholesale, so the slot this send
   * reserved for its answer went with the conversation the user left.
   */
  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrbiActions.sendMessage),
      withLatestFrom(
        this.store.select(selectOrbiSessionId),
        this.store.select(selectOrbiAgentAvailable),
        this.store.select(selectPrimaryAccountHolder)
      ),
      concatMap(([action, sessionId, agentAvailable, patient]) => {
        // Hard gate (PCSA-13165 / PCSA-13452): when the Split flag is off OR the
        // patient has not passed identity verification, the web client makes NO
        // call to the agent service. Emit `sendMessageBlocked` (rather than
        // silently dropping) so the reducer can revert the optimistic Pending
        // status + appended user message — otherwise the UI would be stuck
        // loading forever (no success/failure would ever arrive).
        if (!agentAvailable) {
          return of(OrbiActions.sendMessageBlocked({ sessionId }));
        }
        return this.store.select(selectOrbiSessionId).pipe(
          // The conversation that is active NOW, as this send starts — which for
          // a queued message is not the one `withLatestFrom` captured above.
          take(1),
          filter((activeSessionId) => activeSessionId === sessionId),
          concatMap(() =>
            this.orbiService
              .sendMessage({
                userMessage: action.userMessage,
                sessionId,
                requestContext: action.requestContext,
                ...(patient?.id != null && {
                  patientMasterId: String(patient.id),
                }),
              })
              .pipe(
                map((response) =>
                  OrbiActions.sendMessageSuccess({
                    message: parseOrbiAgentResponse(response),
                    sessionId,
                  })
                ),
                catchError((error: OrbiError) =>
                  of(OrbiActions.sendMessageFailure({ error, sessionId }))
                ),
                takeUntil(this.conversationChanged$)
              )
          )
        );
      })
    )
  );

  /**
   * Persists the session lock (PCSA-13170) when a locking guardrail (EMERGENCY /
   * HARM) arrives, so it survives a browser refresh (the in-memory store is
   * wiped on reload, but sessionStorage is not). The reducer latches the lock in
   * state; this mirror keeps sessionStorage in sync. Non-dispatching.
   */
  persistSessionLock$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrbiActions.sendMessageSuccess),
        tap(({ message }) => {
          if (
            orbiMessageLocksChat(
              message.guardrailTriggered,
              message.guardrailType ?? message.returnType
            )
          ) {
            writeOrbiSessionLock(true);
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Submits response feedback (PCSA-13171) for a specific message in the active
   * conversation. `mergeMap` (not exhaustMap) so feedback on different responses
   * can be in flight concurrently; the sessionId comes from state. Success/
   * failure carry the `messageId` so only the target row locks or re-enables for
   * retry — the active conversation is never interrupted.
   *
   * A CLIENT-COMPOSED response is latched WITHOUT a call. The "How does this
   * work?" explainer (PCSA-13159 AC 6) is assembled in the reducer from constants
   * and carries a local id the gateway never issued, so POSTing it would be a
   * request the server cannot resolve — and the row would then sit un-latched
   * behind a success toast the sheet has already shown. Short-circuiting to
   * success keeps that row behaving like every other one; there is also nothing
   * for the agent to learn from a sentiment on fixed copy.
   */
  submitFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrbiActions.submitOrbiFeedback),
      withLatestFrom(this.store.select(selectOrbiSessionId)),
      mergeMap(([action, sessionId]) => {
        if (action.messageId === ORBI_HOW_IT_WORKS_MESSAGE_ID) {
          return of(
            OrbiActions.submitOrbiFeedbackSuccess({
              messageId: action.messageId,
              feedbackType: action.feedback.feedback as OrbiFeedbackType,
            })
          );
        }
        return this.orbiService.submitFeedback(sessionId, action.feedback).pipe(
          map(() =>
            OrbiActions.submitOrbiFeedbackSuccess({
              messageId: action.messageId,
              feedbackType: action.feedback.feedback as OrbiFeedbackType,
            })
          ),
          catchError((error: OrbiError) =>
            of(
              OrbiActions.submitOrbiFeedbackFailure({
                messageId: action.messageId,
                error,
              })
            )
          )
        );
      })
    )
  );

  /**
   * Mirrors a submitted rating into sessionStorage (PCSA-13312), the same way
   * `persistSessionLock$` mirrors the guardrail lock: the reducer latches it in
   * state, this keeps the copy that survives a browser refresh.
   *
   * Without it the thumbs go back to being clickable after a reload — the
   * in-memory map is gone, while the conversation itself is fetched fresh from
   * history — so an already-rated response could be rated a second time.
   * Only SUCCESS is mirrored, matching the reducer: a failed submission has
   * nothing to remember and must stay actionable for a retry. Non-dispatching.
   */
  persistFeedback$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrbiActions.submitOrbiFeedbackSuccess),
        tap(({ messageId, feedbackType }) =>
          recordOrbiFeedback(messageId, feedbackType)
        )
      ),
    { dispatch: false }
  );

  /**
   * Re-dispatches `sendMessage` from the stashed last user message + context
   * (same sessionId). The single transport effect above then handles it.
   */
  retryLastMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrbiActions.retryLastMessage),
      withLatestFrom(
        this.store.select(selectOrbiLastUserMessage),
        this.store.select(selectOrbiLastRequestContext)
      ),
      filter(
        ([, lastMessage]) =>
          Boolean(lastMessage) && lastMessage.trim().length > 0
      ),
      map(([, userMessage, requestContext]) =>
        OrbiActions.sendMessage({ userMessage, requestContext })
      )
    )
  );
}
